"use client";

import { useUmamiAnalytics } from "@analytics";
import {
	ACCEPTED_IMAGE_TYPES,
	VIDEO_HERO_PROMPT_ID,
} from "@marketing/home/components/HeroControls";
import type { HeroPreset } from "@marketing/home/components/VideoGeneratorHero";
import {
	PanelResizer,
	useLeftPanelWidth,
} from "@saas/ai-image-generator/components/PanelResizer";
import { useSession } from "@saas/auth/hooks/use-session";
import { FrameSlot } from "@saas/image-to-video/components/FrameSlot";
import {
	ACCEPTED_REFERENCE_MEDIA_TYPES,
	ReferenceMediaThumb,
} from "@saas/image-to-video/components/ReferenceMediaThumb";
import { CreditsPaywallModal } from "@saas/payments/components/CreditsPaywallModal";
import { ImageDetailModal } from "@saas/shared/assets/ImageDetailModal";
import { SelectAssetDialog } from "@saas/shared/assets/SelectAssetDialog";
import { UploadPreviewDialog } from "@saas/shared/assets/UploadPreviewDialog";
import { VideoDetailModal } from "@saas/shared/assets/VideoDetailModal";
import { AspectRatioSwatch } from "@saas/shared/components/AspectRatioSwatch";
import { useConfirmationAlert } from "@saas/shared/components/ConfirmationAlertProvider";
import { EFFECTS_CATALOG } from "@saas/shared/config/effects-catalog";
import {
	type FeedView,
	HistoryFeedTabs,
} from "@saas/shared/history/HistoryFeedTabs";
import type { ImageActionLabels } from "@saas/shared/history/HistoryImageActions";
import { ImageHistoryCard } from "@saas/shared/history/ImageHistoryCard";
import { VideoHistoryCard } from "@saas/shared/history/VideoHistoryCard";
import { WorkbenchTemplates } from "@saas/shared/history/WorkbenchTemplates";
import {
	GENERATION_HISTORY_QUERY_KEY,
	type HistoryFilter,
	type ImageHistoryItem,
	type VideoHistoryItem,
	groupHistoryEntries,
} from "@saas/shared/history/types";
import { useImageHistoryActions } from "@saas/shared/history/use-image-history-actions";
import { useVideoFavorite } from "@saas/shared/history/use-video-favorite";
import {
	type VideoHistoryAction,
	useIncomingVideoAction,
} from "@saas/shared/history/video-action-handoff";
import { getVideoHomePage } from "@saas/shared/history/video-home";
import { flashMissingField } from "@saas/shared/lib/flash-missing-field";
import { apiClient } from "@shared/lib/api-client";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@ui/components/button";
import { cn } from "@ui/lib";
import {
	ArrowLeftRightIcon,
	CrownIcon,
	GemIcon,
	Loader2Icon,
	VideoIcon,
} from "lucide-react";
import { useTranslations } from "next-intl";
import dynamic from "next/dynamic";
import Image from "next/image";
import {
	type ReactNode,
	useCallback,
	useEffect,
	useRef,
	useState,
} from "react";
import { useInView } from "react-intersection-observer";
import { toast } from "sonner";
import { usePresetVideoForm } from "../hooks/use-preset-video-form";
import { type EffectExample, EffectExamples } from "./EffectExamples";
import { EffectPickerDialog } from "./EffectPicker";

const EditUploadedImageDialog = dynamic(
	() =>
		import("@saas/shared/assets/EditUploadedImageDialog").then(
			(mod) => mod.EditUploadedImageDialog,
		),
	{ ssr: false },
);

const MAX_IMAGE_SIZE_MB = 30;
const MOBILE_HEADER_HEIGHT = 72;
const MOBILE_SCROLL_GAP = 12;

/** 同 /image-to-video：手机上表单和右栏上下排，滚动容器是窗口，要扣掉顶栏和语言横幅 */
function getMobileScrollTop(element: HTMLElement): number {
	const bannerHeight = Number.parseFloat(
		window
			.getComputedStyle(document.documentElement)
			.getPropertyValue("--language-banner-height"),
	);
	return Math.max(
		0,
		element.getBoundingClientRect().top +
			window.scrollY -
			MOBILE_HEADER_HEIGHT -
			(Number.isNaN(bannerHeight) ? 0 : bannerHeight) -
			MOBILE_SCROLL_GAP,
	);
}

/**
 * Studio 里的特效页（2026-10-23 老公定，先拿 Hotel Lobby 试两周）：外框和 /image-to-video 工作台一样，
 * 左栏是「当前特效卡（H1 + Switch）+ 固定槽位 + 档位 / 比例 / 时长 + Create」，右栏 Examples / Templates /
 * History。网址还是原来的营销页网址，换特效就是跳到另一个特效页，侧栏不动（照 SuperMaker）。
 */
export function EffectWorkbench({
	preset,
	currentHref,
	examples,
	exampleLabels,
	slotImageFit,
	followTemplateExamples = false,
	historyCompatibility,
	effectTabs,
	handOffHistory,
}: {
	slotImageFit?: "cover" | "contain";
	followTemplateExamples?: boolean;
	historyCompatibility?: {
		accepts: (item: VideoHistoryItem) => boolean;
		message: string;
	};
	/** 一个页面有几种效果（Flying Car 的 Ramp / Fly，10/28）：标题卡下面一排切换，外层按选中的换整套 preset */
	effectTabs?: {
		label: string;
		items: { id: string; label: string }[];
		activeId: string;
		onSelect: (id: string) => void;
	};
	/** 配 effectTabs：History 里不归当前这种效果的片交给外层，切过去再回填。返回 true 表示接走了 */
	handOffHistory?: (
		item: VideoHistoryItem,
		action: VideoHistoryAction,
	) => boolean;
	preset: HeroPreset;
	currentHref: string;
	examples: EffectExample[];
	exampleLabels: { title: string; use: string; mute: string; unmute: string };
}) {
	const t = useTranslations("effectWorkbench");
	const tI2V = useTranslations("imageToVideo.client");
	const tImage = useTranslations("aiImageGenerator.client");
	const tAssets = useTranslations("assets");
	const { user } = useSession();
	const { trackEvent } = useUmamiAnalytics();
	const { width: leftPanelWidth, commit: commitLeftPanelWidth } =
		useLeftPanelWidth();
	// History 同 /image-to-video：全部 / 图片 / 视频，默认全部（10/23 老公定，和之前的工作台一样）
	const [historyFilter, setHistoryFilter] = useState<HistoryFilter>("all");
	const {
		form,
		activeTemplate,
		activeQuality,
		note,
		setNote,
		noteMax,
		selectQuality,
		selectTemplate,
		qualityCredits,
		isQualityDisabled,
		userVideo,
		userVideos,
		userVideoMissing,
		addUserVideo,
		presetRatio,
		selectRatio,
		lockedRatio,
		presetDurations,
	} = usePresetVideoForm(preset, historyFilter);
	const {
		inputImages,
		hasRequiredImages,
		canPickAspectRatio,
		setImageAt,
		replaceImage,
		lockedResolutions,
		duration,
		setDuration,
		creditCost,
		isPending,
		isUploading,
		handleGenerate,
		applyHistoryItem,
		latestStartedBatchId,
		resumedBatchId,
		history,
		isHistoryLoading,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
		paywallReason,
		setPaywallReason,
		removeReferenceMedia,
	} = form;
	const effect = EFFECTS_CATALOG.find((item) => item.href === currentHref);
	// 第二槽选填的页（AI Kiss）只传一张图时，Grok 不收比例、成片跟原图走：
	// 只显示一个 Auto，别让人以为选了 9:16（同营销 hero）
	const ratioFollowsImage =
		preset.requiredSlots !== undefined &&
		!canPickAspectRatio &&
		hasRequiredImages;

	const [pickerOpen, setPickerOpen] = useState(false);
	const [assetSlot, setAssetSlot] = useState<number | null>(null);
	const [previewIndex, setPreviewIndex] = useState<number | null>(null);
	const [editIndex, setEditIndex] = useState<number | null>(null);
	const [selectedVideo, setSelectedVideo] = useState<VideoHistoryItem | null>(
		null,
	);
	const [selectedImage, setSelectedImage] = useState<{
		item: ImageHistoryItem;
		imageIndex: number;
	} | null>(null);
	// HD 的锁图标按购买记录画。服务端渲染时购买记录还没拉，客户端 hydrate 时侧栏可能已经拉回来了，
	// 两边对不上整个工作台会被 React 丢掉重画（10/23 e2e 查出），挂载后再画
	const [mounted, setMounted] = useState(false);
	useEffect(() => setMounted(true), []);
	// null = 还没点过右栏的 tab，按有没有作品自动选
	const [feedView, setFeedView] = useState<FeedView | null>(null);
	const [regenerateRequested, setRegenerateRequested] = useState(false);
	const formRef = useRef<HTMLElement>(null);
	const historyScrollRef = useRef<HTMLDivElement>(null);
	const latestBatchRef = useRef<HTMLDivElement>(null);
	const scrolledBatchIdRef = useRef<string | null>(null);
	const queryClient = useQueryClient();
	const { confirm } = useConfirmationAlert();
	const { isFavorite: isVideoFavorite, toggleFavorite: toggleVideoFavorite } =
		useVideoFavorite();
	const imageActions = useImageHistoryActions({
		onDeleted: () => setSelectedImage(null),
	});

	// History 列全部作品（同 /image-to-video）：只列本特效的话，模板改过之前出的片看不到，别的页还在出的
	// 任务也看不到，Create 却被它占着转圈（10/23 Codex 审出来、本地复现过）。
	// 每张卡都有 Reprompt / Regenerate：本特效的片原地回填，别处的片由卡片自己跳回它出生的页面
	// （video-home.ts），放在这里回填会被预设锁套上本页模板
	const isOwnRow = (item: VideoHistoryItem) =>
		getVideoHomePage(item) === currentHref;
	const videoHistory = history.filter(
		(item): item is { type: "video" } & VideoHistoryItem =>
			item.type === "video",
	);
	const hasOwnRows = videoHistory.some(isOwnRow);
	const hasUnsettledRows = videoHistory.some(
		(item) => item.status === "GENERATING" || item.status === "PROCESSING",
	);

	// 升档引导（营销 hero 的同名功能搬过来，Ozil Goal 先用）：这次会话刚出完的那批是带 upgrade 的档
	// （Lite 只有 4 秒），而且还停在这一档，就在档位上面提示一句、给个切档按钮。不自动生成。
	// 没提交过就看刷新后接手的那批（同营销 hero）；接手的可能是别的页出的，只认本特效的片
	const readyItem = latestStartedBatchId
		? videoHistory.find(
				(item) =>
					item.batchId === latestStartedBatchId &&
					item.status === "SUCCESS",
			)
		: resumedBatchId
			? videoHistory.find(
					(item) =>
						item.batchId === resumedBatchId &&
						item.status === "SUCCESS" &&
						isOwnRow(item),
				)
			: undefined;
	const readyQuality = readyItem
		? preset.qualities.find(
				(item) =>
					item.model === readyItem.metadata?.modelId &&
					item.resolution === readyItem.resolution &&
					(item.duration === undefined ||
						item.duration === readyItem.duration),
			)
		: undefined;
	const upgradeTarget =
		readyQuality?.upgrade && activeQuality?.id === readyQuality.id
			? preset.qualities.find(
					(item) => item.id === readyQuality.upgrade?.targetId,
				)
			: undefined;

	// 访客没有作品，只给 Examples / Templates。登录用户有本特效的片、或者有还在出的任务（Create 被它占着，
	// 得让人看到在等什么）就先看我的作品，否则先看本特效的示例
	const activeView: FeedView =
		feedView ??
		(user && (hasOwnRows || hasUnsettledRows || isHistoryLoading)
			? "mine"
			: "examples");

	const { ref: loadMoreRef, inView } = useInView({ rootMargin: "200px" });
	useEffect(() => {
		if (inView && hasNextPage && !isFetchingNextPage) {
			fetchNextPage();
		}
	}, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

	// 开始生成就切到我的作品，停在示例上看不到进度
	useEffect(() => {
		if (latestStartedBatchId) {
			setFeedView("mine");
		}
	}, [latestStartedBatchId]);

	// 新片子只会出现在 All 和 Video 里，别把用户丢在 Image 上干等（同 /image-to-video）
	useEffect(() => {
		if (isPending) {
			setHistoryFilter("all");
		}
	}, [isPending]);

	// 新任务卡片出现后定位一次；轮询刷新不抢滚动位置
	useEffect(() => {
		const card = latestBatchRef.current;
		const container = historyScrollRef.current;
		if (
			!latestStartedBatchId ||
			scrolledBatchIdRef.current === latestStartedBatchId ||
			!card ||
			!container
		) {
			return;
		}
		scrolledBatchIdRef.current = latestStartedBatchId;
		if (window.innerWidth >= 768) {
			container.scrollTo({ top: 0, behavior: "smooth" });
		} else {
			window.scrollTo({
				top: getMobileScrollTop(card),
				behavior: "smooth",
			});
		}
	}, [history, latestStartedBatchId]);

	useEffect(() => {
		if (!regenerateRequested) {
			return;
		}
		setRegenerateRequested(false);
		void handleGenerate();
	}, [regenerateRequested, handleGenerate]);

	// 手机上表单在右栏上面，回填（示例、Reprompt）发生在屏幕外，要滚回去
	const scrollToForm = useCallback(() => {
		if (window.innerWidth < 768 && formRef.current) {
			window.scrollTo(0, getMobileScrollTop(formRef.current));
		}
	}, []);

	const handleSlotFiles = (index: number, files: File[]) => {
		const file = files[0];
		if (!file) {
			return;
		}
		if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
			toast.error(tI2V("invalidImageType"));
			return;
		}
		if (file.size > MAX_IMAGE_SIZE_MB * 1024 * 1024) {
			toast.error(tI2V("imageTooLarge", { size: MAX_IMAGE_SIZE_MB }));
			return;
		}
		setImageAt(index, { previewUrl: URL.createObjectURL(file), file });
	};

	// 素材库读的是登录用户的素材：没登录先弹登录框（同 /image-to-video）
	const openAssetPicker = (index: number) => {
		if (!user) {
			window.dispatchEvent(
				new CustomEvent("show-login", {
					detail: {
						redirectTo:
							window.location.pathname + window.location.search,
					},
				}),
			);
			return;
		}
		setAssetSlot(index);
	};

	// 缺图不弹提示，让空槽闪红框（10/21 老公定）；访客照旧走 handleGenerate，它先弹登录框
	const handleCreateClick = () => {
		if (user && !hasRequiredImages) {
			flashMissingField(
				formRef.current?.querySelector('[data-missing-field="image"]'),
			);
			return;
		}
		// 用户视频模板没传视频：同样闪视频格（AI Dance 的 Your own dance）
		if (user && userVideoMissing) {
			flashMissingField(
				formRef.current?.querySelector('[data-missing-field="video"]'),
			);
			return;
		}
		void handleGenerate();
	};

	const handleDeleteVideo = (item: VideoHistoryItem) => {
		confirm({
			title: tAssets("deleteDialog.title"),
			message: tAssets("deleteDialog.descriptionVideo"),
			confirmLabel: tAssets("deleteDialog.confirm"),
			destructive: true,
			onConfirm: async () => {
				try {
					// 删除按行 id 走，跟模型无关，复用 ai-video 的端点
					const response = await apiClient["ai-video"][
						":taskId"
					].$delete({
						param: { taskId: item.id },
					});
					if (!response.ok) {
						throw new Error("Failed to delete");
					}
					setSelectedVideo(null);
					queryClient.invalidateQueries({
						queryKey: GENERATION_HISTORY_QUERY_KEY,
					});
				} catch (error) {
					console.error(error);
					toast.error("Failed to delete. Please try again.");
				}
			},
		});
	};
	const acceptsHistory = (item: VideoHistoryItem) => {
		if (historyCompatibility && !historyCompatibility.accepts(item)) {
			toast.info(historyCompatibility.message);
			return false;
		}
		return true;
	};
	const handleReprompt = (item: VideoHistoryItem) => {
		if (handOffHistory?.(item, "reprompt") || !acceptsHistory(item)) {
			return;
		}
		applyHistoryItem(item);
		scrollToForm();
	};
	const handleRegenerate = (item: VideoHistoryItem) => {
		if (handOffHistory?.(item, "regenerate") || !acceptsHistory(item)) {
			return;
		}
		applyHistoryItem(item);
		setRegenerateRequested(true);
	};
	// 别的页面（/image-to-video、生图页、My Assets）点了本特效出的片，带着整条记录跳过来
	useIncomingVideoAction((item, action) => {
		if (action === "regenerate") {
			handleRegenerate(item);
		} else {
			handleReprompt(item);
		}
	});
	const actionLabels: ImageActionLabels = {
		generateVideo: tAssets("detail.generateVideo"),
		download: tAssets("buttons.download"),
		more: tAssets("buttons.more"),
		edit: tAssets("buttons.edit"),
		delete: tAssets("delete"),
		favorite: tAssets("buttons.favorite"),
	};

	return (
		// 外框同 /image-to-video：分栏起铺满、左白右灰、中间可拖，左栏宽度和那边共用。
		// 滚动锚点 id="video-generator" 在页面外层那个 div 上
		<div className="studio-theme flex flex-col gap-3 bg-background p-1 max-sm:p-0 sm:gap-4 sm:p-4 studio-split:h-[calc(100vh-var(--language-banner-height,0px))] studio-split:min-h-[560px] studio-split:flex-row studio-split:items-stretch studio-split:gap-0 studio-split:overflow-hidden studio-split:p-0">
			<aside
				ref={formRef}
				className="flex w-full min-h-0 flex-shrink-0 flex-col overflow-hidden rounded-xl border border-border/15 bg-card shadow-sm max-sm:rounded-none max-sm:border-0 max-sm:bg-transparent max-sm:shadow-none studio-split:w-[var(--left-panel-w,412px)] studio-split:max-w-[calc(100%-280px)] studio-split:self-stretch studio-split:rounded-none studio-split:border-0 studio-split:shadow-none"
			>
				<div className="flex-shrink-0 p-3 pb-0 max-sm:px-0 max-sm:pt-0 sm:p-6 studio-split:px-4 studio-split:pt-4">
					{/* 当前特效卡：卡上的字就是 H1，标题不再另写一遍；Switch 打开换特效弹窗 */}
					<div className="flex items-center gap-3 rounded-xl border border-zinc-200 bg-white p-2">
						{effect ? (
							<Image
								src={effect.icon ?? effect.poster}
								alt=""
								width={48}
								height={48}
								sizes="48px"
								className="h-12 w-12 flex-shrink-0 rounded-lg object-cover"
							/>
						) : null}
						<h1 className="min-w-0 flex-1 font-semibold text-base leading-snug">
							{preset.copy.title}
						</h1>
						<button
							type="button"
							onClick={() => setPickerOpen(true)}
							className="flex flex-shrink-0 items-center gap-1.5 rounded-lg px-2.5 py-2 font-medium text-blue-600 text-sm transition-colors hover:bg-blue-50"
						>
							<ArrowLeftRightIcon className="h-4 w-4" />
							{t("switch")}
						</button>
					</div>
					{effectTabs ? (
						<div className="mt-3 flex">
							<Segmented label={effectTabs.label}>
								{effectTabs.items.map((item) => (
									<SegmentButton
										key={item.id}
										active={item.id === effectTabs.activeId}
										onClick={() =>
											effectTabs.onSelect(item.id)
										}
									>
										{item.label}
									</SegmentButton>
								))}
							</Segmented>
						</div>
					) : null}
					<p className="mt-3 text-[13px] text-muted-foreground leading-normal">
						{preset.copy.subtitle}
					</p>
					{/* 原营销 hero 上那句「注册送 15 积分」：只对没登录的人有意义，登录用户首帧就藏（auth-hint.ts） */}
					<p className="mt-1 font-medium text-[13px] text-emerald-600 [[data-authed]_&]:hidden">
						{preset.copy.freeCredits}
					</p>
				</div>

				<div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto overflow-x-hidden px-3 pt-3 pb-3 max-sm:px-0 sm:px-6 sm:pb-6 studio-split:px-4">
					{/* 包一层 shrink-0：Segmented 的 flex-1 是给横排分宽的，直接放在这个竖排里会把高度撑开（AI Kiss 三种吻） */}
					{preset.templates.length > 1 ? (
						<div className="flex shrink-0">
							{/* 模板多了（AI Dance 6 支舞 + 自己传）排成三列，用户视频那个独占一行 */}
							<Segmented
								label={preset.copy.templatesLabel}
								className={
									preset.templates.length > 4
										? "grid grid-cols-3"
										: undefined
								}
							>
								{preset.templates.map((item) => (
									<SegmentButton
										key={item.id}
										active={item.id === activeTemplate?.id}
										onClick={() => selectTemplate(item)}
										className={
											preset.templates.length > 4 &&
											item.userVideo
												? "col-span-3"
												: undefined
										}
									>
										{item.userVideo ? (
											<VideoIcon className="size-3.5" />
										) : null}
										{item.label}
									</SegmentButton>
								))}
							</Segmented>
						</div>
					) : null}

					<div
						className={cn(
							"grid shrink-0 gap-3",
							inputImages.length + (userVideo ? 1 : 0) > 1
								? "grid-cols-2"
								: "grid-cols-1",
						)}
					>
						{inputImages.map((image, index) => (
							<FrameSlot
								key={preset.slotLabels[index]}
								image={image}
								title={preset.slotLabels[index]}
								locked={Boolean(
									preset.lockSlotDefaults &&
										preset.slotDefaults?.[index] &&
										image?.previewUrl ===
											preset.slotDefaults[index],
								)}
								// 可换的默认图（Tiny Desk 乐手）：删了会被上面的 effect 填回去，只能点 Replace 换
								swapLabel={
									!preset.lockSlotDefaults &&
									preset.slotDefaults?.[index] &&
									image?.previewUrl ===
										preset.slotDefaults[index]
										? preset.copy.swapLabel
										: undefined
								}
								selectAssetLabel={tI2V("slot.selectAsset")}
								// 只有一个槽时拉满一整行：整张照片放下，不然全身照只剩中间一截；桌面再高一点，
								// 手机保持 150，不然 375 宽首屏看不到 Create
								className={
									inputImages.length > 1
										? "min-h-[150px] sm:min-h-[170px]"
										: "min-h-[150px] sm:min-h-[220px]"
								}
								fit={
									slotImageFit ??
									(inputImages.length > 1
										? "cover"
										: "contain")
								}
								compact
								onFiles={(files) =>
									handleSlotFiles(index, files)
								}
								onPickAsset={() => openAssetPicker(index)}
								onPreview={() => setPreviewIndex(index)}
								onEdit={() => setEditIndex(index)}
								onRemove={() => setImageAt(index, null)}
							/>
						))}
						{userVideo ? (
							userVideos[0] ? (
								<ReferenceMediaThumb
									media={userVideos[0]}
									processingLabel={tI2V(
										"slot.mediaProcessing",
										{
											percent: Math.round(
												userVideos[0].progress * 100,
											),
										},
									)}
									removeLabel={tI2V("slot.removeVideo")}
									onRemove={() =>
										removeReferenceMedia(userVideos[0].id)
									}
									className="min-h-[150px] rounded-2xl sm:min-h-[170px]"
								/>
							) : (
								<label
									data-missing-field="video"
									className="flex min-h-[150px] cursor-pointer flex-col items-center justify-center rounded-2xl border border-zinc-300 border-dashed bg-zinc-100 p-3 text-center transition-colors hover:border-zinc-400 hover:bg-zinc-200/60 sm:min-h-[170px] sm:p-4"
								>
									<input
										type="file"
										accept={ACCEPTED_REFERENCE_MEDIA_TYPES.filter(
											(type) =>
												type.startsWith("video/") ||
												[".mov", ".m4v"].includes(type),
										).join(",")}
										className="sr-only"
										onChange={(event) => {
											const file =
												event.target.files?.[0];
											event.target.value = "";
											if (file) {
												addUserVideo(file);
											}
										}}
									/>
									<VideoIcon className="mb-2 size-5 text-zinc-700" />
									<span className="font-medium text-sm text-zinc-900">
										{preset.copy.userVideoLabel}
									</span>
									<span className="mt-1 text-xs text-zinc-500">
										{preset.copy.userVideoHint}
									</span>
								</label>
							)
						) : null}
					</div>

					{activeTemplate ? (
						<div className="flex shrink-0 flex-col gap-2">
							<label
								htmlFor={VIDEO_HERO_PROMPT_ID}
								className="font-medium text-sm text-zinc-700"
							>
								{preset.copy.noteLabel}
							</label>
							{/* 字数放进输入框右下角，底部留出 pb-8 不让字压上去 */}
							<div className="relative">
								<textarea
									id={VIDEO_HERO_PROMPT_ID}
									value={note}
									maxLength={noteMax}
									onChange={(event) =>
										setNote(event.target.value)
									}
									placeholder={preset.copy.notePlaceholder}
									rows={3}
									className="block min-h-20 w-full resize-none rounded-2xl border border-zinc-200 bg-white px-4 pt-3 pb-8 text-sm text-zinc-900 outline-none placeholder:text-zinc-400 focus:border-blue-400"
								/>
								<span className="pointer-events-none absolute right-4 bottom-2.5 text-xs text-zinc-400 tabular-nums">
									{note.length} / {noteMax}
								</span>
							</div>
						</div>
					) : null}

					{/* 画质 / 比例 / 时长三组分段紧跟输入框（模型和分辨率藏在画质档里），底部只钉 Create */}
					<div className="flex shrink-0 flex-col gap-3">
						{readyQuality?.upgrade && upgradeTarget ? (
							<div className="flex flex-col gap-2 rounded-xl bg-blue-50 p-3">
								<p className="text-[13px] text-zinc-800 leading-snug">
									{readyQuality.upgrade.text}
								</p>
								<button
									type="button"
									onClick={() => {
										void trackEvent(
											"tier-upgrade-clicked",
											{
												from: readyQuality.id,
												to: upgradeTarget.id,
											},
										);
										selectQuality(upgradeTarget);
									}}
									className="self-start rounded-full bg-blue-500 px-4 py-1.5 font-medium text-sm text-white transition-colors hover:bg-blue-600"
								>
									{readyQuality.upgrade.cta}
								</button>
							</div>
						) : null}
						<Segmented label={preset.copy.qualityLabel}>
							{preset.qualities.map((item) => (
								<SegmentButton
									key={item.id}
									active={item.id === activeQuality?.id}
									onClick={() => selectQuality(item)}
									title={item.hint}
									disabled={isQualityDisabled(item)}
								>
									{item.label}
									{mounted &&
									lockedResolutions.has(item.resolution) ? (
										<CrownIcon className="h-3 w-3 text-amber-500" />
									) : null}
									<span className="font-normal text-[11px] text-zinc-400 tabular-nums">
										{qualityCredits(item)}
									</span>
								</SegmentButton>
							))}
						</Segmented>
						{activeQuality?.note ? (
							<p className="text-xs text-zinc-500">
								{activeQuality.note}
							</p>
						) : null}
						<div className="flex gap-3">
							<Segmented label={preset.copy.ratioLabel}>
								{ratioFollowsImage ? (
									<SegmentButton active onClick={() => {}}>
										<AspectRatioSwatch
											value="auto"
											size={14}
										/>
										{tI2V("settings.autoAspectRatio")}
									</SegmentButton>
								) : null}
								{(ratioFollowsImage
									? []
									: lockedRatio
										? [lockedRatio]
										: preset.aspectRatioOptions
								).map((ratio) => (
									<SegmentButton
										key={ratio}
										active={
											ratio ===
											(lockedRatio ?? presetRatio)
										}
										onClick={() =>
											!lockedRatio && selectRatio(ratio)
										}
									>
										<AspectRatioSwatch
											value={ratio}
											size={14}
											className="border border-current bg-transparent"
										/>
										{ratio}
									</SegmentButton>
								))}
							</Segmented>
							<Segmented label={preset.copy.durationLabel}>
								{presetDurations.map((item) => (
									<SegmentButton
										key={item}
										active={item === duration}
										onClick={() => setDuration(item)}
									>
										{`${item}s`}
									</SegmentButton>
								))}
							</Segmented>
						</div>
					</div>
				</div>

				<div className="flex-shrink-0 bg-card p-4 pt-2 max-sm:bg-transparent max-sm:px-0 sm:p-6 studio-split:px-4 studio-split:pb-4">
					{/* 照 /image-to-video：缺图时半透明，登录用户点了让空槽闪红框，访客照旧能点（弹登录框） */}
					<Button
						type="button"
						onClick={handleCreateClick}
						disabled={isPending}
						className={cn(
							"h-11 w-full rounded-full border-blue-500 bg-blue-500 font-semibold text-sm text-white transition-[background-color,opacity] hover:bg-blue-600 disabled:opacity-70",
							(!hasRequiredImages || userVideoMissing) &&
								"opacity-60",
						)}
					>
						{isPending ? (
							<>
								<Loader2Icon className="mr-2 h-4 w-4 animate-spin" />
								{isUploading
									? tI2V("uploading")
									: tI2V("generating")}
							</>
						) : (
							<>
								{tI2V("create")}
								<GemIcon className="mx-1 ml-2 h-3.5 w-3.5 opacity-80" />
								{activeQuality
									? qualityCredits(activeQuality)
									: creditCost}
							</>
						)}
					</Button>
				</div>
			</aside>

			<PanelResizer
				width={leftPanelWidth}
				onCommit={commitLeftPanelWidth}
				label={tImage("resizePanels")}
			/>

			{/* 右：示例 / 模板 / 我的作品，分栏起灰底 */}
			<main className="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-xl border border-zinc-200/20 bg-white shadow-sm max-sm:overflow-visible max-sm:rounded-none max-sm:border-0 max-sm:bg-transparent max-sm:shadow-none studio-split:rounded-none studio-split:border-0 studio-split:bg-zinc-100 studio-split:shadow-none">
				<div className="flex-shrink-0 p-4 max-sm:px-0 sm:p-6">
					<div className="mx-auto max-w-5xl">
						<HistoryFeedTabs
							view={activeView}
							onViewChange={setFeedView}
							showHistory={!!user}
							filter={historyFilter}
							onFilterChange={(filter) => {
								// 默认 tab 按有没有本特效的视频现算，筛成只看图片后就算成「没作品」跳回示例，先钉住
								setFeedView("mine");
								setHistoryFilter(filter);
							}}
						/>
					</div>
				</div>
				{activeView === "examples" ? (
					<EffectExamples
						key={
							followTemplateExamples
								? activeTemplate?.id
								: undefined
						}
						examples={
							followTemplateExamples
								? (() => {
										const own = examples.filter(
											(item) =>
												item.templateId ===
												activeTemplate?.id,
										);
										// 没有自己样片的模板（AI Dance 的「自己传视频」）放第一条，右栏不空着
										return own.length > 0
											? own
											: examples.slice(0, 1);
									})()
								: examples
						}
						labels={exampleLabels}
						onUse={scrollToForm}
					/>
				) : activeView === "templates" ? (
					// 同 /image-to-video：只放视频特效（10/23 老公定），全部特效走左栏 Switch
					<WorkbenchTemplates
						kind="video"
						className="px-4 max-sm:px-0 sm:px-6"
					/>
				) : history.length > 0 ? (
					<div
						ref={historyScrollRef}
						className="flex-1 overflow-y-auto px-4 pb-6 max-sm:px-0 sm:px-6"
					>
						<div className="mx-auto max-w-5xl space-y-4 sm:space-y-6">
							{groupHistoryEntries(history).map((entry) =>
								entry.kind === "videos" ? (
									<div
										key={entry.items[0].id}
										ref={
											entry.items[0].batchId ===
											latestStartedBatchId
												? latestBatchRef
												: undefined
										}
										data-generation-batch-id={
											entry.items[0].batchId
										}
									>
										<VideoHistoryCard
											items={entry.items}
											onReprompt={handleReprompt}
											onRegenerate={handleRegenerate}
											isRegenerateDisabled={isPending}
											onOpenDetail={setSelectedVideo}
											actionLabels={actionLabels}
											onToggleFavorite={
												toggleVideoFavorite
											}
											isFavorite={isVideoFavorite}
											onDelete={handleDeleteVideo}
										/>
									</div>
								) : (
									<ImageHistoryCard
										key={entry.item.id}
										item={entry.item}
										onOpenDetail={(imageIndex) =>
											setSelectedImage({
												item: entry.item,
												imageIndex,
											})
										}
										onReprompt={() =>
											imageActions.repromptOrRegenerate(
												entry.item,
												"reprompt",
											)
										}
										onRegenerate={() =>
											imageActions.repromptOrRegenerate(
												entry.item,
												"regenerate",
											)
										}
										openDetailLabel={tAssets(
											"detail.title",
										)}
										actionLabels={actionLabels}
										onGenerateVideo={
											imageActions.generateVideo
										}
										onDownload={imageActions.download}
										onEdit={imageActions.edit}
										onDelete={(imageIndex) =>
											imageActions.deleteImage(
												entry.item.id,
												imageIndex,
											)
										}
										onToggleFavorite={(imageIndex) =>
											imageActions.toggleFavorite(
												entry.item,
												imageIndex,
											)
										}
										isImageFavorite={(imageIndex) =>
											imageActions.isImageFavorite(
												entry.item,
												imageIndex,
											)
										}
									/>
								),
							)}
							{hasNextPage ? (
								<div
									ref={loadMoreRef}
									className="flex justify-center py-4"
								>
									{isFetchingNextPage ? (
										<Loader2Icon className="h-5 w-5 animate-spin text-muted-foreground" />
									) : null}
								</div>
							) : null}
						</div>
					</div>
				) : isHistoryLoading ? (
					<div className="flex flex-1 items-center justify-center">
						<Loader2Icon className="h-5 w-5 animate-spin text-muted-foreground" />
					</div>
				) : (
					<div className="flex flex-1 items-center justify-center px-4 pb-6">
						<p className="text-muted-foreground">
							{tAssets("emptyFiltered")}
						</p>
					</div>
				)}
			</main>

			<EffectPickerDialog
				open={pickerOpen}
				onOpenChange={setPickerOpen}
				currentHref={currentHref}
			/>
			<CreditsPaywallModal
				open={paywallReason !== null}
				onOpenChange={(open) => !open && setPaywallReason(null)}
				reason={paywallReason ?? "insufficient-credits"}
			/>
			<SelectAssetDialog
				open={assetSlot !== null}
				onOpenChange={(open) => !open && setAssetSlot(null)}
				remainingSlots={1}
				onConfirm={(urls) => {
					if (assetSlot !== null && urls[0]) {
						setImageAt(assetSlot, { previewUrl: urls[0] });
					}
					setAssetSlot(null);
				}}
			/>
			<UploadPreviewDialog
				imageUrl={
					previewIndex !== null
						? (inputImages[previewIndex]?.previewUrl ?? null)
						: null
				}
				onClose={() => setPreviewIndex(null)}
			/>
			<EditUploadedImageDialog
				open={editIndex !== null}
				imageUrl={
					editIndex !== null
						? (inputImages[editIndex]?.previewUrl ?? null)
						: null
				}
				onOpenChange={(open) => !open && setEditIndex(null)}
				onConfirm={async (blob: Blob) => {
					if (editIndex !== null) {
						replaceImage(
							editIndex,
							new File([blob], "cropped.webp", {
								type: blob.type || "image/webp",
							}),
						);
					}
					setEditIndex(null);
				}}
			/>
			{selectedVideo ? (
				<VideoDetailModal
					isOpen
					onClose={() => setSelectedVideo(null)}
					video={selectedVideo}
					onReprompt={() => handleReprompt(selectedVideo)}
					onRegenerate={() => handleRegenerate(selectedVideo)}
					isFavorite={isVideoFavorite(selectedVideo)}
					onToggleFavorite={() => toggleVideoFavorite(selectedVideo)}
					onDelete={() => handleDeleteVideo(selectedVideo)}
				/>
			) : null}
			{selectedImage ? (
				<ImageDetailModal
					isOpen
					onClose={() => setSelectedImage(null)}
					generation={selectedImage.item}
					initialImageIndex={selectedImage.imageIndex}
					onReprompt={() =>
						imageActions.repromptOrRegenerate(
							selectedImage.item,
							"reprompt",
						)
					}
					onRegenerate={() =>
						imageActions.repromptOrRegenerate(
							selectedImage.item,
							"regenerate",
						)
					}
					onGenerateVideo={imageActions.generateVideo}
					isImageFavorite={(imageIndex) =>
						imageActions.isImageFavorite(
							selectedImage.item,
							imageIndex,
						)
					}
					onToggleFavorite={(imageIndex) =>
						imageActions.toggleFavorite(
							selectedImage.item,
							imageIndex,
						)
					}
					onDelete={(imageIndex) =>
						imageActions.deleteImage(
							selectedImage.item.id,
							imageIndex,
						)
					}
				/>
			) : null}
		</div>
	);
}

/** 灰轨道 + 选中白块，同工作台 tab 的分段样式；label 给读屏用 */
function Segmented({
	label,
	className,
	children,
}: { label: string; className?: string; children: ReactNode }) {
	return (
		<fieldset
			aria-label={label}
			className={cn(
				"flex min-w-0 flex-1 gap-0.5 rounded-xl bg-zinc-100 p-1",
				className,
			)}
		>
			{children}
		</fieldset>
	);
}

function SegmentButton({
	active,
	onClick,
	title,
	disabled,
	className,
	children,
}: {
	active: boolean;
	onClick: () => void;
	title?: string;
	disabled?: boolean;
	className?: string;
	children: ReactNode;
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			title={title}
			disabled={disabled}
			aria-pressed={active}
			className={cn(
				"flex min-w-0 flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg px-2 py-2 font-medium text-[13px] transition-colors disabled:cursor-not-allowed disabled:opacity-40",
				active
					? "bg-white text-zinc-900 shadow-[0_1px_3px_rgba(0,0,0,0.08)]"
					: "text-zinc-500 hover:text-zinc-800",
				className,
			)}
		>
			{children}
		</button>
	);
}
